import type {
  ExtendedProductVariant,
  WovarCustomer,
  WovarProduct,
} from "@commercekit/api";
import type { HTMLComponent } from "@commercekit/react";
import { useErrorAlert } from "@commercekit/react";
import { useCart, useCustomerSimple, useWovarProductMarketingSticker } from "@commercekit/remix";
import { useLocation } from "@remix-run/react";
import { useEffect, useMemo, useRef, useState } from "react";
import WvProductPreview from "../molecules/WvProductPreview";
import WvProductPopularAlternativesBase from "../organisms/WvProductAlternatives";
import WvDiscountCodeBlock from "../molecules/WvDiscountCodeBlock";
import WvProductDetailCard from "../organisms/WvProductDetailCard";
import WvProductProsAndConsBase from "../organisms/WvProductProsAndCons";
import WvProductSpecification from "../organisms/WvProductSpecification";
import WvProductSwiper from "../organisms/WvProductSwiper";

import { useWovarProduct } from "@commercekit/core";
import { SliceZoneC } from "@commercekit/prismic";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useCarousel } from "~/hooks/useCarousel";
import { useWvViewItem } from "~/hooks/useDataLayer";
import { useKlaviyoViewedProduct } from "~/hooks/useKlaviyoTracking";
import { UspBarImageAndText as UspBarImageAndTextBase } from "~/lib/prismic";
import type { ProductPageResultType } from "~/routes-server/$slug/types";
import { safe } from "../atoms/WvSafeRender";
import WvBreadcrumbs from "../atoms/WvBreadcrumbs";
import WvAddToCartPopup from "../molecules/WvAddToCartPopup";
import WvProductInformation from "../organisms/WvProductInformation";
import WvRelatedProductsBase from "../organisms/WvRelatedProducts";
import { useYotpo } from "~/hooks/useYotpo";
import { buildProductGalleryVideos } from "~/lib/youtubeUtils";

// Secondary PDP sections: a crash here must not take the product page down (WOV-2200).
const UspBarImageAndText = safe("UspBarImageAndText", UspBarImageAndTextBase);
const WvProductProsAndCons = safe("WvProductProsAndCons", WvProductProsAndConsBase);
const WvRelatedProducts = safe("WvRelatedProducts", WvRelatedProductsBase);
const WvProductPopularAlternatives = safe(
  "WvProductPopularAlternatives",
  WvProductPopularAlternativesBase
);

const WvProductPage: HTMLComponent<{
  productPageResult: ProductPageResultType;
  canonicalsUrl: string;
  captchaSiteKey: string;
}> = ({ productPageResult, canonicalsUrl, captchaSiteKey }) => {
  // TODO: Look into "productInfo" to ensure that it contains the selected uomProperty when the product was added to cart before the popup was opened.
  const {
    productInfo,
    uspBarImageAndTextSlices,
    paymentMethods,
    relatedProducts,
    popularAlternatives,
    productSwitcher,
    isYotpoEnabled,
    discountCodeBlock,
  } = productPageResult;

  const { customer } = useCustomerSimple();

  const [showPreview, setShowPreview] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCartPopup, setShowCartPopup] = useState(false);
  const [selectedUomAndQuantity, setSelectedUomAndQuantity] = useState("");
  const [updateSelectedProductVariant, setUpdateSelectedProductVariant] =
    useState<ExtendedProductVariant | undefined>(undefined);
  const lastTrackedProductId = useRef<string | null>(null);
  const { show } = useErrorAlert();
  const gtmViewItem = useWvViewItem();
  const klaviyoViewedProduct = useKlaviyoViewedProduct();
  const yotpo = useYotpo();
  useEffect(() => {
    if (productInfo && productInfo.id !== lastTrackedProductId.current) {
      gtmViewItem(productInfo);
      klaviyoViewedProduct(productInfo);
      lastTrackedProductId.current = productInfo.id;
    }
  }, [gtmViewItem,
    klaviyoViewedProduct,
    productInfo]);

  const { customFields: specifications } = useWovarProduct(
    productInfo,
    updateSelectedProductVariant
  );

  useEffect(() => {
    if (productInfo)
      setUpdateSelectedProductVariant({
        name: productInfo?.name,
        slug: productInfo?.slug,
        description: productInfo?.description,
        marketingProperties: productInfo?.marketingProperties,
        advantages: productInfo?.advantages,
        disadvantages: productInfo?.disadvantages,
        unitsOfMeasure: productInfo?.unitsOfMeasure,
        id: productInfo?.id,
        sku: productInfo?.sku,
        defaultImage: {
          url: productInfo?.defaultImage?.urlOriginal,
          altText: productInfo?.defaultImage?.altText,
        },
        images: productInfo?.images,
        priceInclTax: productInfo?.priceInclTax,
        priceExclTax: productInfo?.priceExclTax,
        salePriceInclTax: productInfo?.salePriceInclTax,
        salePriceExclTax: productInfo?.salePriceExclTax,
        stock: productInfo?.stockLevel,
        customFields: productInfo?.customFields,
        uomProperty: "",
      });
  }, [productInfo]);

  const location = useLocation();
  useEffect(() => {
    setShowCartPopup(false);
  }, [location]);

  useEffect(() => {
    if (window.yotpoWidgetsContainer?.initWidgets) {
      setTimeout(() => {
        window.yotpoWidgetsContainer.initWidgets?.();
      }, 15);
    }
  }, [])

  const { clearResult, resultQueue } = useCart();

  const cartError = useMemo(() => {
    if (!resultQueue?.length) return null;
    const error = resultQueue?.find(
      (result) => result?.actionFail && productInfo?.id === result?.id
    );
    return error?.error ? error?.error : null;
  }, [resultQueue, productInfo?.id]);

  useEffect(() => {
    if (cartError) show(cartError, "warning");
  }, [cartError, show]);

  const isSuccess = useMemo(() => {
    return (
      resultQueue?.length &&
      resultQueue?.find((result) => result?.id === productInfo?.id) &&
      !cartError
    );
  }, [resultQueue, cartError, productInfo?.id]);

  useEffect(() => {
    if (isSuccess && !showCartPopup) {
      clearResult();
      setShowCartPopup(true);
    }
  }, [isSuccess, clearResult, showCartPopup, resultQueue]);

  const { stickerTitle } = useWovarProductMarketingSticker(productInfo);

  const productAdvantages = useMemo(() => {
    return updateSelectedProductVariant
      ? updateSelectedProductVariant?.advantages?.reduce(
          (advantages: string[], advantage) => {
            if (advantage?.value?.stringValue)
              advantages = [...advantages, advantage?.value?.stringValue];
            return advantages;
          },
          []
        )
      : productInfo?.advantages?.reduce((advantages: string[], advantage) => {
          if (advantage?.value?.stringValue)
            advantages = [...advantages, advantage?.value?.stringValue];
          return advantages;
        }, []);
  }, [productInfo, updateSelectedProductVariant]);

  const productDisadvantages = useMemo(() => {
    return updateSelectedProductVariant
      ? updateSelectedProductVariant?.disadvantages?.reduce(
          (disavantages: string[], disavantage) => {
            if (disavantage?.value?.stringValue)
              disavantages = [...disavantages, disavantage?.value?.stringValue];
            return disavantages;
          },
          []
        )
      : productInfo?.disadvantages?.reduce(
          (disavantages: string[], disavantage) => {
            if (disavantage?.value?.stringValue)
              disavantages = [...disavantages, disavantage?.value?.stringValue];
            return disavantages;
          },
          []
        );
  }, [productInfo, updateSelectedProductVariant]);

  const productBreadcrumbs = useMemo(() => {
    if (!productInfo) return [];
    const { id, name, path, slug } = productInfo;

    return productInfo?.categories?.length
      ? [...productInfo?.categories, { id, name, path, slug }]
          ?.map(({ id, name, slug }) => ({ id, name, path: slug }))
          ?.filter((category) => category?.id && category?.name)
      : [];
  }, [productInfo]);

  const wheelGestures = WheelGesturesPlugin();
  const mainSwiper = useCarousel(
    {
      containScroll: "trimSnaps",
      startIndex: 0,
      draggable: true,
      loop: true,
    },
    [wheelGestures]
  );

  const displayImages = useMemo(
    () =>
      updateSelectedProductVariant?.images
        ? updateSelectedProductVariant.images
        : productInfo?.images,
    [productInfo?.images, updateSelectedProductVariant?.images]
  );

  const galleryVideos = useMemo(
    () => buildProductGalleryVideos(productInfo?.videos),
    [productInfo?.videos]
  );

  const hasGalleryMedia = useMemo(() => {
    const validImageSlides =
      displayImages?.filter((img) => img?.urlOriginal).length ?? 0;
    return validImageSlides > 0 || galleryVideos.length > 0;
  }, [displayImages, galleryVideos]);

  // Product schema is JSON-LD, built server-side in product.server.ts
  return (
    productInfo && (
      <article className="pdp">
        <div className="container">
          <WvBreadcrumbs breadcrumbs={productBreadcrumbs} mobileMarginBottom="9.5px" />

          <header>
            <div className="d-md-flex has-rating">
              <h1
                className="pb-2 pt-md-2 pb-md-3 heading underlined sans-serif strong mdl left"
                style={{ backgroundPositionY: "112% !important" }}
              >
                {updateSelectedProductVariant
                  ? updateSelectedProductVariant?.name
                  : productInfo?.name}
              </h1>
              <div
                className="yotpo-widget-instance pt-md-2 ml-md-3"
                data-yotpo-instance-id={yotpo?.starRating}
                data-yotpo-product-id={updateSelectedProductVariant?.sku ?? productInfo?.sku}
              ></div>
            </div>
          </header>

          <div className="product-details-wrapper">
            <div className="product-details-wrapper__description">
              {productInfo && hasGalleryMedia && (
                <WvProductSwiper
                  onPreview={(index: number) => {
                    setActiveIndex(index);
                    setShowPreview(true);
                  }}
                  marketingSticker={stickerTitle}
                  images={(displayImages ?? []) as WovarProduct["images"]}
                  videos={galleryVideos}
                  embla={mainSwiper}
                />
              )}

              <div className="product-details-wrapper__payment-details">
                <WvProductDetailCard
                  product={productInfo}
                  onOpenCartPopup={() => setShowCartPopup(true)}
                  paymentMethods={paymentMethods}
                  updateSelectedProductVariant={setUpdateSelectedProductVariant}
                  updateSelectedUomAndQuantity={setSelectedUomAndQuantity}
                  productSwitcher={productSwitcher}
                  captchaSiteKey={captchaSiteKey}
                />
                {discountCodeBlock ? <WvDiscountCodeBlock block={discountCodeBlock} /> : null}
              </div>

              {(productAdvantages?.length || productDisadvantages?.length) ? (
                <div className="pros-cons-wrapper">
                  <WvProductProsAndCons
                    productName={
                      updateSelectedProductVariant
                        ? updateSelectedProductVariant?.name
                        : productInfo?.name
                    }
                    advantages={productAdvantages}
                    disadvantages={productDisadvantages}
                    categories={productBreadcrumbs}
                  />
                </div>
              ) : null}

              <WvProductInformation.FullWidth
                description={
                  updateSelectedProductVariant
                    ? updateSelectedProductVariant?.description
                    : productInfo?.description
                }
                productName={
                  updateSelectedProductVariant
                    ? updateSelectedProductVariant?.name
                    : productInfo?.name
                }
                isSplit={false}
                sku={productInfo?.sku}
              />

              <WvProductSpecification specifications={specifications} />

              <WvProductSpecification assets={productInfo.assets} />

              {relatedProducts?.length > 0 && (
                <WvRelatedProducts
                  products={relatedProducts}
                  customer={customer as WovarCustomer}
                />
              )}
            </div>

            <div className="product-details-wrapper__payment-details--alt">
              <WvProductDetailCard
                product={productInfo}
                onOpenCartPopup={() => setShowCartPopup(true)}
                paymentMethods={paymentMethods}
                updateSelectedProductVariant={setUpdateSelectedProductVariant}
                updateSelectedUomAndQuantity={setSelectedUomAndQuantity}
                productSwitcher={productSwitcher}
                customer={customer as WovarCustomer}
                captchaSiteKey={captchaSiteKey}
              />
              {discountCodeBlock ? <WvDiscountCodeBlock block={discountCodeBlock} /> : null}
            </div>
          </div>
          <div className="my-5 pdp__uspbar">
            <SliceZoneC
              slices={uspBarImageAndTextSlices}
              components={{
                usp_bar_image_and_text: UspBarImageAndText,
              }}
              defaultComponent={({ slice }) => <div>{slice.slice_type}</div>}
            />
          </div>

          <section className="mt-5 pdp__extra-related">
            {popularAlternatives?.length ? (
              <WvProductPopularAlternatives
                products={popularAlternatives}
                itemListId="pdp__extra-related"
                itemListName="Product Detail Popular Alternatives"
                customer={customer as WovarCustomer}
              />
            ) : null}
          </section>
        </div>

        {showPreview && (
          <WvProductPreview
            name={
              updateSelectedProductVariant
                ? updateSelectedProductVariant?.name
                : productInfo?.name
            }
            activeIndex={activeIndex}
            onClose={() => {
              setShowPreview(false);
            }}
            images={(displayImages ?? []) as WovarProduct["images"]}
            videos={galleryVideos}
          />
        )}
        {showCartPopup && (
          <WvAddToCartPopup
            onClose={() => {
              clearResult();
              setShowCartPopup(false);
            }}
            relatedProducts={relatedProducts}
            updateSelectedProductVariant={updateSelectedProductVariant}
            selectedUomAndQuantity={selectedUomAndQuantity}
          />
        )}
        {yotpo && isYotpoEnabled ? (
          <>
            <div
              className="yotpo-widget-instance"
              data-yotpo-instance-id={yotpo.widgetId}
              data-yotpo-product-id={productInfo?.sku}
              data-yotpo-name={productInfo?.name}
              data-yotpo-url={canonicalsUrl}
              data-yotpo-image-url={productInfo?.images[0]?.urlThumbnail}
              data-yotpo-price={productInfo?.priceExclTax?.value}
              data-yotpo-currency={productInfo?.priceExclTax?.currencyCode}
              data-yotpo-description={productInfo?.description}
            ></div>
          </>
        ) : null}
      </article>
    )
  );
};

export default WvProductPage;
